import { magnific } from "@client/content/hocs";
import { GalleryMagnificOptions, IFrameMagnificOptions, NoneMagnificOptions } from "@client/content/hocs/magnific";
import { globals } from "@client/globals";
import { type MagnificOptions } from "@client/lib/jquery/magnific";
import { goTo, isSystemRoute } from "@client/lib/navigation";
import { type GenericMap } from "@client/types";
import { getTooltipAttributes } from "@common/contexts/Tooltip";
import React, { useCallback } from "react";

export type LinkType = "regular_link_type" | "new_window_link_type" | "lightbox_link_type" | "gallery_link_type";

export interface LinkProps {
  className?: string;
  isNoRedirect?: boolean;
  href?: string;
  htmlId?: string;
  id?: string;
  preventRedirect?: boolean;
  style?: React.CSSProperties;
  title?: string;
  tooltip?: string;
  type?: LinkType;

  onClick?: (e: React.MouseEvent) => void;
  children?: any;
}

const LINK_TYPE_TO_OPTIONS: GenericMap<MagnificOptions> = {
  lightbox_link_type: IFrameMagnificOptions,
  gallery_link_type: GalleryMagnificOptions
};

const MagnificA = magnific<HTMLAnchorElement>("a");
const MagnificDiv = magnific<HTMLAnchorElement>("div");
const Link = React.forwardRef(
  (
    { id, isNoRedirect, type, preventRedirect, onClick, htmlId, tooltip, ...props }: LinkProps,
    ref: React.RefObject<any>
  ) => {
    const _onClick = useCallback(
      (e: React.MouseEvent) => {
        if (globals.mode === "builder" || (preventRedirect && !e.ctrlKey)) {
          e.preventDefault();
          return;
        }

        if (!props.href) e.preventDefault();

        if (props.href && type === "regular_link_type") {
          if (props.href.startsWith("/") && !isSystemRoute(props.href)) {
            e.preventDefault();

            goTo(props.href);
            return;
          }
        }

        onClick && onClick(e);
      },
      [preventRedirect, onClick, type, props.href]
    );

    const magnificOptions = React.useMemo(
      () =>
        type && type in LINK_TYPE_TO_OPTIONS && !preventRedirect ? LINK_TYPE_TO_OPTIONS[type] : NoneMagnificOptions,
      [type, preventRedirect]
    );

    const Component = isNoRedirect ? MagnificDiv : MagnificA;
    return (
      <Component
        ref={ref}
        {...props}
        data-noredirect={isNoRedirect}
        id={htmlId}
        onClick={magnificOptions === NoneMagnificOptions ? _onClick : undefined}
        magnificOptions={magnificOptions}
        target={type === "new_window_link_type" ? "_blank" : undefined}
        {...getTooltipAttributes(tooltip)}
      />
    );
  }
);
export default Link;
